import { makeAutoObservable } from 'mobx';
import { FileLibrary } from '../../stores/libraries/FileLibrary';
import { DataStore } from '../DataStore';
import { IValidatedPageLibrary } from './IValidatedPageLibrary';
import { AxiosError, AxiosResponse } from 'axios';
import { UIStore } from '../UIStore';
import * as Models from '../../models';

export class FilesPageLibrary implements IValidatedPageLibrary
{
	private dataStore: DataStore;
	private uiState: UIStore;

	constructor(dataStore: DataStore, ui: UIStore)
	{
		this.dataStore = dataStore;
		this.uiState = ui;
		makeAutoObservable(this,
			{
			}, { autoBind: true });
	}

	public MaxStorageSize: number = 70 * 1024 * 1024;

	public CurrentStorageSize: number = 0;
	setCurrentStorageSize(value: number)
	{
		this.CurrentStorageSize = value;
	}

	public ChunkSize = 1048576; // 1 Mb


	/** Группировка файлов по их типу. Один тип - один компонент FileDropZoneUploaderList. Внутри типа - список загружаемых файлов */
	public FileLists: Map<string, FileLibrary[]> = new Map<string, FileLibrary[]>();

	setFileList(FileTypeId: string, files: FileLibrary[]) { this.FileLists.set(FileTypeId, files); }

	deleteFileList(FileTypeId: string) { this.FileLists.delete(FileTypeId); }

	clearFileList() { this.FileLists.clear(); }

	public shortFileSize(size: number)
	{
		if (size < 1024) return size + " б";
		size = Math.round(size / 1024);
		if (size < 1024) return size + " кб";
		size = Math.round(size / 1024);
		return size + " мб";
	}


	findFileInList(fileTypeId: string, fileId: string): FileLibrary
	{
		let fs: FileLibrary | null = null;
		this.FileLists.get(fileTypeId)?.some(f =>
		{
			if (f.FileId === fileId)
			{
				fs = f;
				return true;
			}
			return false;
		});
		if (!fs) throw new Error("fs is null");
		return fs;
	}

	public SortedFileTypeArray: Models.StatementFileTypeModel[] = [];
	setSortedFileTypeArray(value: Models.StatementFileTypeModel[]) { this.SortedFileTypeArray = value; }

	private getSortedFileTypeArray(): Models.StatementFileTypeModel[]
	{
		let retVal = new Map<string, Models.StatementFileTypeModel>();
		let retArray: Models.StatementFileTypeModel[] = [];

		this.dataStore.uiState.register.SelectedServiceCategories.forEach((cat, key) =>
		{
			let itemArray = this.dataStore.CategoryFileTypes.get(key);
			if (itemArray)
			{
				itemArray.forEach((ft) =>
				{
					if (!retVal.has(ft.Id))
					{
						retVal.set(ft.Id, ft);
						retArray.push(ft);
					}
				});
			}
		});

		return retArray
			.sort((a, b) =>
			{
				if (a.OrderNumber > b.OrderNumber) return 1;
				if (a.OrderNumber < b.OrderNumber) return -1;
				return 0;
			});
	}

	UpdateFileTypes()
	{
		// TODO: Проверить
		const sortedArray = this.getSortedFileTypeArray();

		let oldMap = new Map<string, Models.StatementFileTypeModel>();
		this.SortedFileTypeArray.forEach((val) => oldMap.set(val.Id, val));

		let newMap = new Map<string, Models.StatementFileTypeModel>();
		sortedArray.forEach((val) => newMap.set(val.Id, val));


		// добавляем нехватающие
		sortedArray.forEach((val) =>
		{
			if (!oldMap.has(val.Id))
			{
				this.SortedFileTypeArray.push(val);
				this.FileLists.set(val.Id, []);
			}
		});

		// список индексов для удаления
		let ids: Map<string, string> = new Map<string, string>();

		// удаляем лишние файлы
		this.SortedFileTypeArray.forEach((val) =>
		{
			if (!newMap.has(val.Id))
			{
				ids.set(val.Id, "");
				let deletingFiles = this.FileLists.get(val.Id);
				if (deletingFiles)
				{
					deletingFiles.forEach((f) => this.DeleteFileStore(val.Id, f));
				}
				this.FileLists.delete(val.Id);
			}
		});

		// удаляем лишние записи о файлах
		for (var i = 0; i < this.SortedFileTypeArray.length; i++)
		{
			if (ids.has(this.SortedFileTypeArray[i].Id))
			{
				this.SortedFileTypeArray.splice(i, 1);
				i--;
			}
		}

	}

	/**
	 * Определяет, нужно ли удалять загруженные ранее файлы при удалении указанной категории 
	 * @param categoryId удаляемая категория
	 */
	IsNeedDeleteFileTypes(categoryId: string): boolean
	{
		// получаем список категорий, которые остаются отмеченными
		let stayCategoryIds: Map<string, string> = new Map<string, string>();
		this.uiState.register.SelectedServiceCategories.forEach((val) =>
		{
			if (val.Category.Id !== categoryId && val.Operation !== Models.StatementCategoryOperationEnum.None) stayCategoryIds.set(val.Category.Id, "");
		});

		// получаем список типов файлов для удаляемой категории
		let types: Models.StatementFileTypeModel[] | undefined = this.dataStore.CategoryFileTypes.get(categoryId);
		if (!types) return false;

		// переносим список типов файлов удаляемой категории в словарик для ускорения поиска
		let typesMap: Map<string, string> = new Map<string, string>();
		types.forEach((val) => typesMap.set(val.Id, ""));

		// Удаляем из списка те типы файлов, которые используются в остающихся категориях
		this.dataStore.CategoryFileTypes.forEach((val, catId) =>
		{
			if (catId === categoryId) return;
			if (!stayCategoryIds.has(catId)) return;
			val.forEach((val) => typesMap.delete(val.Id));
		});

		// получаем количество загруженных файлов из оставшихся типов файлов
		let fileCount: number = 0;
		typesMap.forEach((val, key) =>
		{
			let list = this.FileLists.get(key);
			if (list) fileCount += list.length;
		});

		// если файлы были, сообщаем об этом
		return fileCount > 0;
	}

	AddFileLibrary(FileTypeId: string, fileObject: File): FileLibrary
	{
		let list = this.FileLists.get(FileTypeId);
		if (!list)
		{
			this.FileLists.set(FileTypeId, []);
			list = this.FileLists.get(FileTypeId);
		}
		if (!list) throw new Error("list is null");
		this.setCurrentStorageSize(this.CurrentStorageSize + fileObject.size);
		let fs = new FileLibrary(this, this.dataStore, FileTypeId, fileObject);
		list.push(fs);
		return fs;
	}

	AddFileLibraryFromServer(FileTypeId: string, FileName: string, FileSize: number, FileExistsOnServer: boolean, FileState: Models.FileStateEnum, FileId: string, PageCount: number)
	{
		let list = this.FileLists.get(FileTypeId);
		if (!list)
		{
			this.FileLists.set(FileTypeId, []);
			list = this.FileLists.get(FileTypeId);
		}
		if (!list) throw new Error("list is null");
		this.setCurrentStorageSize(this.CurrentStorageSize + FileSize);
		let fs = new FileLibrary(this, this.dataStore, FileTypeId, undefined, FileName, FileSize, FileExistsOnServer, FileState, FileId, PageCount);
		list.push(fs);
	}

	DeleteFileStore(FileTypeId: string, s: FileLibrary)
	{
		let deletingFileSize: number = s.FileSize;
		if (!s.FileExistsOnServer)
		{
			this.deleteFileStoreInternal(FileTypeId, s, deletingFileSize);
		}
		else
		{
			s.setFileState(Models.FileStateEnum.Waiting);

			this.dataStore.DeleteFile(s.FileTypeId, s.FileId)
				.then((res: AxiosResponse<Models.FormDeletedResponse>) =>
				{
					s.setFileExistsOnServer(false);
					this.dataStore.ProcessFormDeleted(res.data);
					this.deleteFileStoreInternal(FileTypeId, s, deletingFileSize);
				})
				.catch((res: AxiosError) =>
				{
					s.setFileState(Models.FileStateEnum.Faulted);

					// Ругаемся
					if (res.response?.data)
					{
						let error = (res.response.data as Models.ErrorResponse);
						s.setFileErrorMessage(error.MessageToUser + " (" + error.ActivityId + ")");
						return;
					}
					else s.setFileErrorMessage("Ошибка удаления файла");
				});
		}
	}

	private deleteFileStoreInternal(FileTypeId: string, s: FileLibrary, deletingFileSize: number)
	{
		let list = this.FileLists.get(FileTypeId);
		if (!list) throw new Error("List not found");
		var index = list.indexOf(s);
		if (index !== -1) list.splice(index, 1);
		this.setCurrentStorageSize(this.CurrentStorageSize - deletingFileSize);
		this.Validate();
	}

	//	#region	IValidatedPageLibrary
	public ValidationMessage: string = "";
	setValidationMessage(value: string) { this.ValidationMessage = value; }

	public IsValid: boolean = false;
	setIsValid(value: boolean) { this.IsValid = value; }

	public IsValidated: boolean = false;
	setIsValidated(value: boolean) { this.IsValidated = value; }

	// получаем типы, относящиеся к добавляемым услугам (Даже если идет уточнение, то в рамках уточнения может быть добавление)
	// также получаем типы, в которых есть хоть один загруженный файл
	public GetTypesForNewInformation(): Map<string, string>
	{
		let typesArray: Map<string, string> = new Map<string, string>();
		this.dataStore.uiState.register.SelectedServiceCategories.forEach((cat, key) =>
		{
			// Пропускаем все опрации кроме добавления
			if (cat.Operation !== Models.StatementCategoryOperationEnum.NewInformation) return;

			// получаем словарик типов
			this.dataStore.CategoryFileTypes.get(key)?.forEach((ft) =>
			{
				// пропускаем безусловно необязательные типы
				if (!ft.IsRequired) return;

				typesArray.set(ft.Id, "");
			});
		});

		this.FileLists.forEach((arr, id) =>
		{
			if (arr.length > 0) typesArray.set(id, "");
		});

		return typesArray;
	}

	/**
	 * Проверка данных на странице с файлами
	 * @param onlyCheck Если True, то проверки делаются только ради вывода статистики в контроле ValidationStatusComponent, при этом поля с ошибками не помечаются флажками ошибок
	 */
	public Validate(onlyCheck?: boolean)
	{
		// очищаем строку ошибки
		this.setValidationMessage("");
		// изначально признаём всю форму валидной, а потом всё проверяем
		this.IsValid = true;

		// если форма уже в режиме Readonly, то все поля валидны (иначе бы форма не перешла в состояние readonly)
		if (this.uiState.IsDisabled) { return this.IsValid; }

		// если идет полноценная валидация, помечаем всю форму что она прошла валидацию (чтобы стали показываться красные и зеленые кружочки)
		if (!onlyCheck) this.setIsValidated(true);

		// получаем типы, относящиеся к добавляемым услугам
		// также получаем типы, в которых есть хоть один загруженный файл
		let typesArray = this.GetTypesForNewInformation();

		// если добавления услуг нет, то все валидно
		if (typesArray.size === 0) { return this.IsValid; }

		// если типов файлов совсем нет (когда не выбрана ни одна услуга)
		if (this.FileLists.size === 0)
		{
			this.IsValid = false;
			this.setValidationMessage("Приложены не все документы");
			return this.IsValid;
		}

		// Проходим циклом по всем типам файлов
		this.FileLists.forEach((val, key) =>
		{
			// проверяем список файлов конкретного типа
			const res = this.ValidateFileList(typesArray, val, key, onlyCheck);
			// проставляем флажок невалидности
			this.IsValid = this.IsValid && res.Result;
			// если проверка вернула текст, его надо показать в контроле ValidationStatusComponent
			if (!res.Result && res.Message) this.setValidationMessage(res.Message);
		});

		return this.IsValid;

	}

	public ValidateFileList(typesArray: Map<string, string>, list: FileLibrary[], key: string, onlyCheck?: boolean): ValidationResult
	{
		let retVal = new ValidationResult();

		if (list.length === 0)
		{
			// пропускаем типы, которые необязательны
			if (!typesArray.has(key)) return retVal;

			retVal.Message = "Приложены не все документы";
			retVal.Result = false;
			return retVal;
		}

		list.forEach((item) =>
		{
			// каждый элемент должен быть уже загружен
			if (item.FileState !== Models.FileStateEnum.Succeed)
			{
				//retVal.Message = "Присуствуют незагруженные файлы";
				retVal.Message = "Приложены не все документы";
				retVal.Result = false;
			}

			// каждый элемент должен содержать страницы
			if (item.PageCount < 1)
			{
				retVal.Message = "Не указано количество листов";
				retVal.Result = false;
			}
		});

		return retVal;
	}
	//	#endregion
}

export class ValidationResult
{
	public Result: boolean = true;
	public Message: string | undefined = undefined;
}
